import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import { ViewMode } from '@playbooks/action-cards-utils'

import { trackFunnelViewSlideChanged } from '../services/trackService'
import useMountEffect from './useMountEffect'
import { SliderManager, Step, StepType } from '../types'

const STEPS_CORRECTION = 1

function useHorizontalSliderMethods({
  viewMode,
  playbookId,
  sliderManagerRef,
  setIsFinishButtonVisible,
}: {
  viewMode: ViewMode | undefined
  playbookId: string
  sliderManagerRef: React.RefObject<SliderManager | null>
  setIsFinishButtonVisible: (flag: boolean) => void
}) {
  const [currentStep, setCurrentStep] = useState<Step | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const scrollContainer = useRef<HTMLDivElement>(null)
  const stepRef = useRef(0)

  const mapScopesToSteps = useCallback(() => {
    if (viewMode !== ViewMode.FUNNEL) return
    if (!scrollContainer.current) return

    let stepCounter = 0
    // to ensure that slides are loaded and we can get the correct number of them
    const scopes = [...scrollContainer.current!.querySelectorAll('[data-separator-scope]')]

    const mappedSteps = scopes.map(x => {
      return {
        type: stepCounter === 0 ? StepType.FIRST : null,
        step: stepCounter++,
        stepsNumber: scopes.length,
        separatorId: Number.parseInt(x.getAttribute('data-separator-scope') || '0') || 0,
      }
    })

    mappedSteps.push({
      type: StepType.LAST,
      step: mappedSteps.length,
      stepsNumber: mappedSteps.length,
      separatorId: 0,
    })

    setSteps(mappedSteps)
    return mappedSteps
  }, [viewMode])

  useMountEffect(() => {
    const mappedSteps = mapScopesToSteps()
    if (!mappedSteps) return
    trackFunnelViewSlideChanged(playbookId, mappedSteps[0].separatorId)
  })

  useImperativeHandle(sliderManagerRef, () => {
    return {
      nextSlide: () => {
        const steps = mapScopesToSteps()
        if (!steps) return

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (stepRef.current + STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[stepRef.current].type !== StepType.LAST) stepRef.current++

        const currentStepNumber = steps[stepRef.current]

        trackFunnelViewSlideChanged(playbookId, currentStepNumber.separatorId)

        setIsFinishButtonVisible(steps[stepRef.current].type === StepType.LAST)
        setCurrentStep(currentStepNumber)

        return currentStepNumber
      },
      prevSlide: () => {
        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (stepRef.current - STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[stepRef.current].type !== StepType.FIRST) stepRef.current--

        const currentStepNumber = steps[stepRef.current]

        setIsFinishButtonVisible(currentStepNumber.type === StepType.LAST)
        setCurrentStep(currentStepNumber)

        return currentStepNumber
      },
      getCurrentStep: () => {
        return steps[stepRef.current]
      },
    }
  })

  return { scrollContainer, step: currentStep }
}

export default useHorizontalSliderMethods
